import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { zodResolver } from "@hookform/resolvers/zod";
import { CatButton } from "blocks-nextjs/blocks-react/components/Button/Button";
import { CatContextualMenu } from "blocks-nextjs/blocks-react/components/ContextualMenu/ContextualMenu";
import { CatDropdown } from "blocks-nextjs/blocks-react/components/Dropdown/Dropdown";
import { CatFieldset } from "blocks-nextjs/blocks-react/components/Fieldset/Fieldset";
import { CatHeading } from "blocks-nextjs/blocks-react/components/Heading/Heading";
import { CatIconClock } from "blocks-nextjs/blocks-react/components/Icons/Clock/Clock";
import { CatIconClose } from "blocks-nextjs/blocks-react/components/Icons/Close/Close"; // added
import { CatInputField } from "blocks-nextjs/blocks-react/components/InputField/InputField";
import { CatList } from "blocks-nextjs/blocks-react/components/List/List";
import { CatListItem } from "blocks-nextjs/blocks-react/components/ListItem/ListItem";
import { CatLoadingIndicator } from "blocks-nextjs/blocks-react/components/LoadingIndicator/LoadingIndicator";
import { CatRadioField } from "blocks-nextjs/blocks-react/components/RadioField/RadioField";
import { CatSegmentedControl } from "blocks-nextjs/blocks-react/components/SegmentedControl/SegmentedControl";
import { CatSegmentedControlItem } from "blocks-nextjs/blocks-react/components/SegmentedControlItem/SegmentedControlItem";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { z } from "zod";
import chargingIcon from "../../assets/Charging.gif";
import { getBatteryIcon } from "../../lib/getBatteryIcon";
import { getAssetImageIcon, getCESTLocalTime } from "../../pages/Projects/ProjectDetails.helper";
import { useAssetsChargingOptions, useFutureChargingOptions } from "../../services/query/eflow-charge-operation-planner-api.query";
import { safelyStopImmediatePropagation } from "../../shared/eventUtils";
import { getDefaultTimeUnits, getHourMinuteForTimezone, getLocalTimeInCESTFormat, getPlaceholderTime, getTimezoneAbbr, sortByChargingOptions } from "../../shared/helper";
import { formatTimestampWithTimezone } from "../../shared/timestampHelpers";
import useAppConfigStore from "../../store/useAppConfigStore";
import { useChargingPersistenceStore } from "../../store/useChargingPersistenceStore";
import APIError from "../APIError/APIError";
import NoChargingDataMessage from "../DataError/NoChargingDataMessage";
import NoDataMessage from "../DataError/NoDataMessage";
import ChargingOptionDrawer from "../Drawers/ChargingOptionDrawer";
import ChargingOptionDetails from "./ChargingOptionDetails";
import { prepareFutureChargeOptionsRequestBody } from "./ChargingOptions.helper";
import FutureChargingOptionDetails from "./FutureChargingOptionDetails";
import "./OpsPlanner.css";
const LiveLocalCESTTime = ({ timezone = "Europe/Amsterdam" }) => {
    const [time, setTime] = useState(getLocalTimeInCESTFormat(timezone));
    useEffect(() => {
        const interval = setInterval(() => {
            setTime(getLocalTimeInCESTFormat(timezone));
        }, 1000);
        return () => clearInterval(interval);
    }, [timezone]);
    return `${time} ${getTimezoneAbbr(timezone)}`;
};
const OpsPlanner = ({ addressDetails = [], assetsDetails = [], assetsIds = [], fetchChargingData = false, projectMetaData = {}, refetchDataFromApi, setOpsPlannerDate }) => {
    const TimeSchema = z.object({
        time: z.string().trim().min(1, "Time is required")
    });
    const ValidationSchema = TimeSchema;
    const defaultValuesInfo = {
        time: ""
    };
    const { control, getValues, setValue } = useForm({
        defaultValues: defaultValuesInfo,
        mode: "onBlur",
        resolver: zodResolver(ValidationSchema)
    });
    const { getAssetState, persistFutureResp, persistTime } = useChargingPersistenceStore((s) => ({
        getAssetState: s.getAssetState,
        persistFutureResp: s.setFutureChargingOptionsResponse,
        persistTime: s.setTime
    }));
    // Get persisted state reactively from the store
    const persistedState = useChargingPersistenceStore((s) => s.getAssetState("opsPlanner"));
    const STALE_FUTURE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minute
    const initialTimePanel = "Departure";
    const panelIndexMap = {
        Arrival: 1,
        Completion: 2,
        Departure: 0
    };
    const initialTimeIndex = panelIndexMap[initialTimePanel];
    // const [timePanel, setTimePanel] = useState<Panel>(initialTimePanel);
    // const [timePanelActiveIndex, setTimePanelActiveIndex] = useState<number>(initialTimeIndex);
    const [timeError, setTimeError] = useState("");
    const [chargeFilter, setChargeFilter] = useState("80");
    const [sortByFilter, setSortByFilter] = useState("fastest");
    const [chargingDrawerActive, setChargingDrawerActive] = useState(false);
    const [selectedOptionDetail, setSelectedOptionDetail] = useState(null);
    const [fetchFutureChargeOptions, setFetchFutureChargeOptions] = useState(false);
    const [showFutureChargingOptionDetails, setShowFutureChargingOptionDetails] = useState(false);
    const [isLoadingFutureChargingOptionDetails, setIsLoadingFutureChargingOptionDetails] = useState(false);
    const [applyBtnClicked, setApplyBtnClicked] = useState(false);
    const [timeUnits, setTimeUnits] = useState(getDefaultTimeUnits(projectMetaData?.timezone));
    const [selectedTime, setSelectedTime] = useState("Select time");
    const [openSelectTime] = useState(false);
    const selectTimeRef = useRef(null);
    const SELECT_TIME_DELAY_MS = 1000;
    const suppressNextOpenRef = useRef(false);
    const [isReloadingChargingOptions, setIsReloadingChargingOptions] = useState(false);
    // Future charging request body & applied filter are frozen at Apply time
    const [futureRequestBody, setFutureRequestBody] = useState(null);
    const [appliedChargeFilter, setAppliedChargeFilter] = useState(chargeFilter);
    // Persisted future response (for hydration without immediate refetch)
    const [hydratedFutureData, setHydratedFutureData] = useState(null);
    // Track whether we're using persisted future data (suppresses base query)
    const [isUsingPersistedData, setIsUsingPersistedData] = useState(false);
    // Refs for state management like ChargingOptionsWrapper
    const initialBaseFetchRef = useRef(false);
    const staleRefetchTriggeredRef = useRef(false);
    const userChangedTimePanelRef = useRef(false);
    const timePanelActiveIndexRef = useRef(0);
    const selectedPanelRef = useRef("Departure");
    const lastProjectIdRef = useRef(projectMetaData?.projectId);
    // Ref to track if time panel was closed
    const isTimePanelClosedRef = useRef(false);
    // Initialization / hydration effect - similar to ChargingOptionsWrapper
    useEffect(() => {
        const state = persistedState;
        if (!projectMetaData?.projectId)
            return;
        // Reset refs when project changes
        if (lastProjectIdRef.current !== projectMetaData?.projectId) {
            lastProjectIdRef.current = projectMetaData?.projectId;
            userChangedTimePanelRef.current = false;
            initialBaseFetchRef.current = false;
            staleRefetchTriggeredRef.current = false;
        }
        const hasAppliedTime = state.time && state.timePanel && state.selectedTimeLabel !== "Select time";
        if (state.showFuture && hasAppliedTime) {
            const now = Date.now();
            const isStale = !state.lastFutureFetchAt || now - state.lastFutureFetchAt > STALE_FUTURE_THRESHOLD_MS;
            // Only hydrate if we haven't already set the selected time (to avoid resetting user input)
            if (selectedTime === "Select time") {
                // Hydrate UI from persisted state
                setValue("time", state.time);
                if (state.timeUnits)
                    setTimeUnits(state.timeUnits);
                setSelectedTime(state.selectedTimeLabel);
                // Only set panel if user hasn't manually changed it
                if (!userChangedTimePanelRef.current) {
                    selectedPanelRef.current = state.timePanel;
                    const index = panelIndexMap[state.timePanel] || 0;
                    timePanelActiveIndexRef.current = index;
                }
            }
            // Handle stale data refetch
            if (isStale) {
                if (!staleRefetchTriggeredRef.current) {
                    staleRefetchTriggeredRef.current = true;
                    setShowFutureChargingOptionDetails(true);
                    setFetchFutureChargeOptions(true);
                    setIsLoadingFutureChargingOptionDetails(true);
                }
            }
            else {
                // Only hydrate cached data when we are NOT in the middle of an apply-triggered fetch
                if (!applyBtnClicked && !fetchFutureChargeOptions) {
                    staleRefetchTriggeredRef.current = false;
                    const persistedFuture = state.futureChargingOptionsResponse;
                    if (persistedFuture) {
                        setHydratedFutureData(persistedFuture);
                        setShowFutureChargingOptionDetails(true);
                        setIsLoadingFutureChargingOptionDetails(false);
                    }
                    else {
                        setShowFutureChargingOptionDetails(false);
                        setIsLoadingFutureChargingOptionDetails(false);
                    }
                }
            }
            setIsUsingPersistedData(true);
        }
        else {
            // No future time selected - ensure base data is loaded
            setIsUsingPersistedData(false);
            // Only reset if we're not in future mode already
            if (!showFutureChargingOptionDetails) {
                setSelectedTime("Select time");
                if (!userChangedTimePanelRef.current) {
                    selectedPanelRef.current = initialTimePanel;
                    timePanelActiveIndexRef.current = initialTimeIndex;
                }
                setTimeUnits(getDefaultTimeUnits());
                setValue("time", "");
                // Only reset charge filter if not closing time panel
                if (!isTimePanelClosedRef.current) {
                    setChargeFilter("80");
                }
                setHydratedFutureData(null);
            }
            // Auto-trigger base fetch on mount if no prior time selection
            if (!hasAppliedTime && !initialBaseFetchRef.current && fetchChargingData) {
                initialBaseFetchRef.current = true;
                setIsReloadingChargingOptions(true);
            }
        }
        // Reset the ref after effect
        if (isTimePanelClosedRef.current) {
            isTimePanelClosedRef.current = false;
        }
    }, [projectMetaData?.projectId, selectedTime, showFutureChargingOptionDetails, fetchChargingData]);
    // Control charging options API call - only skip if using persisted data after apply
    const shouldFetchChargingData = !isUsingPersistedData && fetchChargingData;
    const { data: assetChargingOptionsData = {}, refetch: refetchChargingOptions, status: assetChargingOptionsApiStatus } = useAssetsChargingOptions(assetsIds, projectMetaData?.projectId, chargeFilter, shouldFetchChargingData, refetchDataFromApi);
    const [searchParams, setSearchParams] = useSearchParams();
    const mergeParams = (updates) => {
        const params = Object.fromEntries(searchParams.entries());
        Object.entries(updates).forEach(([k, v]) => {
            if (v === null)
                delete params[k];
            else
                params[k] = v;
        });
        setSearchParams(params, { replace: true });
    };
    const { appConfig } = useAppConfigStore((state) => ({
        appConfig: state.appConfig
    }));
    useEffect(() => {
        let lastUpdatedSoCTimestamp = assetChargingOptionsData?.assets?.[0]?.chargeOptions?.[0]?.essMetaData?.lastUpdatedSoCTimestamp;
        if (lastUpdatedSoCTimestamp) {
            if (!lastUpdatedSoCTimestamp.includes("T") && !lastUpdatedSoCTimestamp.includes("-")) {
                lastUpdatedSoCTimestamp = new Date().toISOString().split("T")[0] + "T" + lastUpdatedSoCTimestamp;
            }
            const formattedDate = getCESTLocalTime(lastUpdatedSoCTimestamp, "dd MMM yyyy");
            setOpsPlannerDate(formattedDate);
        }
        if (assetChargingOptionsApiStatus === "success" || assetChargingOptionsApiStatus === "error") {
            setIsLoadingFutureChargingOptionDetails(false);
        }
    }, [assetChargingOptionsData, assetChargingOptionsApiStatus]);
    useEffect(() => {
        // Reset form state when project changes
        setChargeFilter("80");
        setSelectedTime("Select time");
        setValue("time", "");
        setShowFutureChargingOptionDetails(false);
        setFetchFutureChargeOptions(false);
        setTimeUnits(getDefaultTimeUnits());
        selectedPanelRef.current = initialTimePanel;
        timePanelActiveIndexRef.current = panelIndexMap[initialTimePanel];
    }, [projectMetaData?.projectId]);
    const handleChargeFilter = (event) => {
        const value = event.target.value;
        if (value === chargeFilter)
            return;
        setChargeFilter(value);
        if (showFutureChargingOptionDetails && selectedTime !== "Select time") {
            // Future mode: trigger future refetch only
            setAppliedChargeFilter(value); // Update applied filter for future API
            setApplyBtnClicked(true); // Enable the future hook
            setFetchFutureChargeOptions(true);
            setIsLoadingFutureChargingOptionDetails(true);
        }
        else {
            // Normal mode: show loader and let query key change trigger fetch
            setIsReloadingChargingOptions(true);
        }
    };
    const validateFutureCESTTime = (rawInput, meridiem) => {
        const CEST_TZ = "Europe/Amsterdam";
        const raw = rawInput.trim();
        if (!raw || !raw.includes(":"))
            return { canProceed: false };
        const [hStr, mStr] = raw.split(":");
        let hour = Number(hStr);
        const minute = Number(mStr);
        if (Number.isNaN(hour) || Number.isNaN(minute))
            return { canProceed: false };
        if (meridiem === "PM" && hour < 12)
            hour += 12;
        if (meridiem === "AM" && hour === 12)
            hour = 0;
        const { hour: nowH, minute: nowM } = getHourMinuteForTimezone(CEST_TZ);
        // Check for 12:00 AM specifically
        if (hour === 0 && minute === 0 && nowH >= 0 && nowM > 0) {
            return { canProceed: false, error: "SOC prediction is only available for today's date" };
        }
        if (hour * 60 + minute <= nowH * 60 + nowM) {
            return { canProceed: false, error: "Entered time should be greater than current site time" };
        }
        return { canProceed: true };
    };
    const handleApply = () => {
        const { canProceed, error } = validateFutureCESTTime(getValues("time") || "", timeUnits);
        if (!canProceed) {
            if (error)
                setTimeError(error);
            return;
        }
        setTimeError("");
        const CEST_TZ = "Europe/Amsterdam";
        const timeLabel = `${selectedPanelRef.current} time: ${getValues("time")} ${timeUnits} ${getTimezoneAbbr(CEST_TZ)}`;
        // Set flags to use persisted data and disable base fetch
        setIsUsingPersistedData(true);
        // Cache the time selection data
        persistTime("opsPlanner", {
            selectedTimeLabel: timeLabel,
            showFuture: true,
            time: getValues("time"),
            timePanel: selectedPanelRef.current,
            timeUnits: timeUnits
        });
        // Reset stale refetch trigger for new fetch
        staleRefetchTriggeredRef.current = false;
        // Freeze inputs for the future query so re-renders don't recreate request body
        const newBody = prepareFutureChargeOptionsRequestBody(assetsIds, projectMetaData?.projectId, getValues("time"), timeUnits, timePanelActiveIndexRef.current);
        // Clear any previously hydrated future data so loader shows for this fresh request
        setHydratedFutureData(null);
        setFutureRequestBody(newBody);
        setAppliedChargeFilter(chargeFilter); // only refetch future data when Apply pressed
        setApplyBtnClicked(true); // gate hook enable
        setFetchFutureChargeOptions(true); // keep existing flag for loader logic
        setIsLoadingFutureChargingOptionDetails(true);
        setShowFutureChargingOptionDetails(true);
        setSelectedTime(timeLabel);
        selectTimeRef.current.close();
        setIsReloadingChargingOptions(false);
    };
    const onChargingDrawerClose = () => {
        mergeParams({ ChargingDrawer: null });
        setSelectedOptionDetail(null);
        setChargingDrawerActive(false);
    };
    const isThroughChargeStationMatched = (throughChargeStation, assetName) => {
        const formattedAssetName = assetName.toLowerCase().replace(/\s+/g, "_");
        return throughChargeStation.some(({ sourceName }) => sourceName.toLowerCase() === formattedAssetName);
    };
    const onChargeOptionClick = (assetInfo, chargeOption, rowIndex) => {
        setChargingDrawerActive(true);
        const { essMetaData = {}, throughChargeStation = [] } = chargeOption;
        const mapAddressDetails = addressDetails?.filter(({ assetId, assetName }) => assetId === assetInfo.assetId ||
            assetId === essMetaData?.assetId ||
            isThroughChargeStationMatched(throughChargeStation, assetName));
        const mapDirectionRequest = [];
        const srcAddressDetails = mapAddressDetails?.find((obj) => obj.assetId === essMetaData?.assetId);
        const destAddressDetails = mapAddressDetails?.filter((obj) => obj.assetId !== essMetaData?.assetId);
        //request between src asset to others
        destAddressDetails.map((obj, index) => {
            mapDirectionRequest.push({
                destAssetName: obj.assetName,
                destination: {
                    location: { latLng: { latitude: obj?.location[0], longitude: obj?.location[1] } }
                },
                id: `route-${index}`,
                origin: {
                    location: {
                        latLng: { latitude: srcAddressDetails?.location[0], longitude: srcAddressDetails?.location[1] }
                    }
                },
                srcAssetName: srcAddressDetails.assetName,
                srcAssetNameFull: srcAddressDetails.assetName,
                srcAssetNameToOthers: true
            });
        });
        //request between others
        destAddressDetails.forEach((obj, index) => {
            if (index < destAddressDetails.length - 1) {
                mapDirectionRequest.push({
                    destAssetName: destAddressDetails[index + 1].assetName,
                    destination: {
                        location: {
                            latLng: {
                                latitude: destAddressDetails[index + 1]?.location[0],
                                longitude: destAddressDetails[index + 1]?.location[1]
                            }
                        }
                    },
                    id: `route-${destAddressDetails.length + index}`,
                    origin: {
                        location: {
                            latLng: {
                                latitude: destAddressDetails[0]?.location[0],
                                longitude: destAddressDetails[0]?.location[1]
                            }
                        }
                    },
                    srcAssetName: destAddressDetails[0].assetName,
                    srcAssetNameFull: srcAddressDetails.assetName,
                    srcAssetNameToOthers: false
                });
            }
        });
        assetInfo.streetAddress = srcAddressDetails.streetAddress;
        setSelectedOptionDetail({
            addressDetails: mapAddressDetails,
            assetInfo,
            chargeOption,
            mapDirectionRequest,
            projectMetaData,
            rowIndex
        });
        const chargingOptionsContainer = document.getElementById("charging-options-" + rowIndex);
        chargingOptionsContainer.scrollTo({ behavior: "smooth", left: 0 });
    };
    const selectedChargeRow = selectedOptionDetail?.rowIndex;
    const selectedChargeOption = selectedOptionDetail?.chargeOption?.chargeId;
    // Only enable future charging hook AFTER Apply has been clicked & we have a frozen request body
    const shouldFetchFuture = applyBtnClicked && !!futureRequestBody;
    const { data: assetFutureChargingOptionDetailsData = {}, refetch: refetchFutureChargingOptionDetails, status: assetFutureChargingOptionDetailsApiStatus } = useFutureChargingOptions(shouldFetchFuture, futureRequestBody, appliedChargeFilter);
    const renderFutureChargingOptionDetails = (assetDetail, chargingOptionIndex) => {
        // Prefer live query state if active, otherwise fall back to hydrated cached data
        if (assetFutureChargingOptionDetailsApiStatus === "pending" && !hydratedFutureData) {
            return (_jsx("div", { className: "flex justify-center items-center h-full min-h-[28vh] w-full", children: _jsx(CatLoadingIndicator, {}) }));
        }
        else if (assetFutureChargingOptionDetailsApiStatus === "error") {
            return _jsx(APIError, { refreshHandler: refetchFutureChargingOptionDetails });
        }
        const effectiveData = assetFutureChargingOptionDetailsApiStatus === "success"
            ? assetFutureChargingOptionDetailsData
            : hydratedFutureData;
        if (!effectiveData)
            return null; // nothing to show
        if (assetFutureChargingOptionDetailsApiStatus === "success") {
            if (Object.keys(effectiveData).length === 0 || effectiveData?.assets?.length === 0) {
                return _jsx(NoDataMessage, {});
            }
            const { assets = [] } = effectiveData;
            const filteredOptionsData = assets?.filter((chargingOptions) => chargingOptions.assetId === assetDetail.assetId);
            const chargingOptionsData = filteredOptionsData.map(({ chargeOptions, ...rest }) => {
                const optionsWithId = chargeOptions.map((option, index) => ({
                    ...option,
                    chargeId: `${rest.assetId}-option-${index}` // or use uuid if needed
                }));
                return {
                    ...rest,
                    chargeOptions: sortByChargingOptions(optionsWithId, sortByFilter)
                };
            });
            const hasValidChargeOptions = Array.isArray(chargingOptionsData) &&
                chargingOptionsData.length > 0 &&
                chargingOptionsData.every((item) => Array.isArray(item.chargeOptions) && item.chargeOptions.length > 0);
            if (!hasValidChargeOptions) {
                return _jsx(NoChargingDataMessage, {});
            }
            return (_jsx(FutureChargingOptionDetails, { assetDetail: assetDetail, chargeFilter: chargeFilter, chargingOptionIndex: chargingOptionIndex, chargingOptionsData: chargingOptionsData, offset: projectMetaData?.offset, onChargeOptionClick: onChargeOptionClick, selectedChargeOption: selectedChargeOption, selectedChargeRow: selectedChargeRow, time: getValues("time"), timePanel: selectedPanelRef.current, timeUnits: timeUnits, timezone: projectMetaData?.timezone }));
        }
        // Hydrated data path (api status not success but we have cached data)
        if (effectiveData &&
            Object.keys(effectiveData).length > 0 &&
            effectiveData.assets &&
            effectiveData.assets.length > 0) {
            const { assets = [] } = effectiveData;
            const filteredOptionsData = assets?.filter((chargingOptions) => chargingOptions.assetId === assetDetail.assetId);
            const chargingOptionsData = filteredOptionsData.map(({ chargeOptions, ...rest }) => {
                const optionsWithId = chargeOptions.map((option, index) => ({
                    ...option,
                    chargeId: `${rest.assetId}-option-${index}`
                }));
                return { ...rest, chargeOptions: sortByChargingOptions(optionsWithId, sortByFilter) };
            });
            return (_jsx(FutureChargingOptionDetails, { assetDetail: assetDetail, chargeFilter: chargeFilter, chargingOptionIndex: chargingOptionIndex, chargingOptionsData: chargingOptionsData, offset: projectMetaData?.offset, onChargeOptionClick: onChargeOptionClick, selectedChargeOption: selectedChargeOption, selectedChargeRow: selectedChargeRow, time: getValues("time"), timePanel: selectedPanelRef.current, timeUnits: timeUnits, timezone: projectMetaData?.timezone }));
        }
    };
    const renderChargingOptions = (assetDetail, chargingOptionIndex) => {
        if (assetChargingOptionsApiStatus === "pending") {
            return (_jsx("div", { className: "flex justify-center items-center h-full min-h-[28vh] w-full", children: _jsx(CatLoadingIndicator, {}) }));
        }
        else if (assetChargingOptionsApiStatus === "error") {
            return _jsx(APIError, { refreshHandler: refetchChargingOptions });
        }
        else if (assetChargingOptionsApiStatus === "success") {
            if (Object.keys(assetChargingOptionsData).length === 0 || assetChargingOptionsData?.assets?.length === 0) {
                return _jsx(NoDataMessage, {});
            }
            const { assets = [] } = assetChargingOptionsData;
            const filteredOptionsData = assets?.filter((chargingOptions) => chargingOptions.assetId === assetDetail.assetId);
            const chargingOptionsData = filteredOptionsData.map(({ chargeOptions, ...rest }) => {
                const optionsWithId = chargeOptions.map((option, index) => ({
                    ...option,
                    chargeId: `${rest.assetId}-option-${index}` // or use uuid if needed
                }));
                return {
                    ...rest,
                    chargeOptions: sortByChargingOptions(optionsWithId, sortByFilter)
                };
            });
            const hasValidChargeOptions = Array.isArray(chargingOptionsData) &&
                chargingOptionsData.length > 0 &&
                chargingOptionsData.every((item) => Array.isArray(item.chargeOptions) && item.chargeOptions.length > 0);
            if (!hasValidChargeOptions) {
                return _jsx(NoChargingDataMessage, {});
            }
            return (_jsx(ChargingOptionDetails, { assetDetail: assetDetail, chargeFilter: chargeFilter, chargingOptionIndex: chargingOptionIndex, chargingOptionsData: chargingOptionsData, offset: projectMetaData?.offset, onChargeOptionClick: onChargeOptionClick, selectedChargeOption: selectedChargeOption, selectedChargeRow: selectedChargeRow, timezone: projectMetaData?.timezone }));
        }
    };
    const getOpsChargingDetails = () => {
        return assetsDetails?.map((assetInfo, index) => {
            // Get timezone from timeZone, timezone, or offset field with fallback
            const timezone = projectMetaData?.timeZone || projectMetaData?.timezone || projectMetaData?.offset;
            // Use shared helper to format timestamps (handles both ISO and time-only formats)
            const { formattedTime: formattedLastReportedTime, timezoneAbbr } = formatTimestampWithTimezone(assetInfo?.timestamp, timezone, "us-date", // Format: MM/dd/yyyy; HH:mm
            (ts) => getCESTLocalTime(ts, "MM/dd/yyyy; HH:mm", projectMetaData?.offset, timezone) || "");
            // Get forecastedSoC from the future charging API response
            const effectiveData = assetFutureChargingOptionDetailsApiStatus === "success"
                ? assetFutureChargingOptionDetailsData
                : hydratedFutureData;
            const matchingAsset = effectiveData?.assets?.find((asset) => asset.assetId === assetInfo.assetId);
            const forecastedSoC = matchingAsset?.forecastedSoC;
            // Get previously stored forecastedSoC from persistence store as fallback
            const persistedForecastedSoC = persistedState?.forecastedSoCByAsset?.[assetInfo.assetId];
            // Determine what value to display
            // Priority: fresh forecastedSoC > persisted forecastedSoC > current soc
            const displaySoC = showFutureChargingOptionDetails || isLoadingFutureChargingOptionDetails
                ? forecastedSoC !== null && forecastedSoC !== undefined
                    ? Number(forecastedSoC)
                    : persistedForecastedSoC !== null && persistedForecastedSoC !== undefined
                        ? Number(persistedForecastedSoC)
                        : assetInfo.soc
                : assetInfo.soc;
            return (_jsxs("div", { className: "flex bg-white border-[2px] border-[#e9e5e5] border-solid rounded-xl", children: [_jsxs("div", { className: "flex w-[267px] min-h-[276px] shrink-0 border-r-[2px] border-[#e9e5e5] border-solid", children: [_jsx("div", { className: "w-[40px]", children: _jsxs(CatContextualMenu, { "data-test": "asset-portfolio-context-menu", position: "bottom", children: [_jsx(CatButton, { hideText: true, slot: "trigger", variant: "ghost", children: "Menu" }), _jsx("div", { className: "list-item-link" })] }) }), _jsxs("div", { className: "flex flex-col justify-center", children: [_jsx("div", { className: "flex justify-between mx-3 align-center", children: _jsxs("div", { className: "flex battery-details", children: [_jsx("img", { alt: "Assets", className: "w-24 p-2 mr-2 min-h-50 min-w-50 flex align-center", "data-test": `${assetInfo?.assetSerialNumber}-image`, src: getAssetImageIcon(assetInfo) }), _jsxs("div", { className: "flex items-center", children: [_jsx("span", { className: "mr-[7px] text-sm", children: displaySoC !== null && displaySoC !== undefined
                                                                ? `${Math.round(displaySoC)}%`
                                                                : "" }), _jsx("span", { children: assetInfo.activity === "Charging" ? (_jsx("img", { alt: "charging.gif", className: "w-[24px] h-[14px]", src: chargingIcon })) : (getBatteryIcon(Number(displaySoC || 0))) })] })] }) }), _jsx("div", { className: "text-lg font-semibold", children: assetInfo?.assetName }), _jsxs("div", { className: "text-sm text-gray-500", children: [assetInfo?.assetMetadata?.serialNumber, " | ", assetInfo?.assetMetadata?.make, " |", " ", assetInfo?.assetMetadata?.model] }), _jsxs("div", { className: "text-xs text-gray-400", children: ["Last update: ", formattedLastReportedTime, " ", timezoneAbbr] })] })] }), _jsx("div", { className: "chargingOptionsContainer w-full overflow-x-auto", id: `charging-options-${index}`, children: isLoadingFutureChargingOptionDetails || isReloadingChargingOptions ? (_jsx("div", { className: "flex justify-center items-center h-full min-h-[28vh] w-full", children: _jsx(CatLoadingIndicator, {}) })) : showFutureChargingOptionDetails ? (_jsx(_Fragment, { children: renderFutureChargingOptionDetails(assetInfo, index) })) : (renderChargingOptions(assetInfo, index)) })] }, `ops-charging-details-${index}`));
        });
    };
    const handleSelectTimeClick = (event) => {
        if (suppressNextOpenRef.current) {
            suppressNextOpenRef.current = false;
            event.preventDefault();
            setTimeout(() => {
                try {
                    selectTimeRef.current?.close();
                }
                catch {
                    /* noop */
                }
            }, 0);
            return;
        }
        if (openSelectTime) {
            event.preventDefault();
            setTimeout(() => {
                selectTimeRef.current.open();
            }, SELECT_TIME_DELAY_MS);
        }
    };
    const timePanelChange = (event) => {
        timePanelActiveIndexRef.current = event.detail.activeIndex;
        selectedPanelRef.current = ["Departure", "Arrival", "Completion"][event.detail.activeIndex];
    };
    // Clear manual base loader once base query completes
    useEffect(() => {
        if (isReloadingChargingOptions &&
            (assetChargingOptionsApiStatus === "success" || assetChargingOptionsApiStatus === "error")) {
            setIsReloadingChargingOptions(false);
        }
    }, [assetChargingOptionsApiStatus, isReloadingChargingOptions]);
    // Clear future loader and persist response on successful future fetch
    useEffect(() => {
        if (fetchFutureChargeOptions) {
            // One-shot consumption
            setFetchFutureChargeOptions(false);
            setShowFutureChargingOptionDetails(true);
        }
        if (assetFutureChargingOptionDetailsApiStatus === "success" ||
            assetFutureChargingOptionDetailsApiStatus === "error") {
            setIsLoadingFutureChargingOptionDetails(false);
            setApplyBtnClicked(false); // disable hook for subsequent re-renders until next Apply
        }
        if (assetFutureChargingOptionDetailsApiStatus === "success") {
            persistFutureResp("opsPlanner", assetFutureChargingOptionDetailsData);
            // Store forecastedSoC values by assetId for caching during loading states
            const dataWithForecast = assetFutureChargingOptionDetailsData;
            if (dataWithForecast?.assets) {
                const forecastedSoCByAsset = {};
                dataWithForecast.assets.forEach((asset) => {
                    if (asset.forecastedSoC !== null && asset.forecastedSoC !== undefined) {
                        forecastedSoCByAsset[asset.assetId] = asset.forecastedSoC;
                    }
                });
                // Persist the forecasted SoC values directly using store's set method
                if (Object.keys(forecastedSoCByAsset).length > 0) {
                    // Use Zustand's set to update the store directly
                    useChargingPersistenceStore.setState((state) => {
                        const currentOpsPlanner = state.assetChargingByAsset.opsPlanner || getAssetState("opsPlanner");
                        return {
                            assetChargingByAsset: {
                                ...state.assetChargingByAsset,
                                opsPlanner: {
                                    ...currentOpsPlanner,
                                    forecastedSoCByAsset
                                }
                            }
                        };
                    });
                }
            }
        }
    }, [
        assetFutureChargingOptionDetailsData,
        assetFutureChargingOptionDetailsApiStatus,
        fetchFutureChargeOptions,
        persistFutureResp,
        getAssetState,
        persistTime
    ]);
    return (_jsxs("div", { className: "ops-planner-container bg-[#F1F1F1]", "data-test": "ops-planner-container", children: [_jsxs("div", { className: "p-4 text-black font-sans space-y-4", children: [_jsxs("div", { className: "flex justify-between", children: [_jsx(CatHeading, { className: "flex", "data-test": "ops-planner-page-title", variant: "title", children: "Charging Options" }), _jsx("div", { className: "flex gap-4", children: _jsxs(CatFieldset, { orientation: "horizontal", children: [appConfig?.release_1_1?.showSocFutureChargingOpsPlanner && (_jsxs("div", { className: "local-time-cest-format", "data-test": "ops-planner-cest-time", children: ["Site time: ", _jsx(LiveLocalCESTTime, { timezone: projectMetaData?.timezone })] })), _jsx(CatRadioField, { checked: chargeFilter === "80", className: "mr-2", name: "charging-percentage", onBlRadioChange: handleChargeFilter, value: "80", children: "Charge to 80%" }), _jsx(CatRadioField, { checked: chargeFilter === "100", className: "mr-2", name: "charging-percentage", onBlRadioChange: handleChargeFilter, value: "100", children: "Charge to 100%" }), _jsx(CatDropdown, { ariaDescribedBy: "charging-options-dropdown-field", className: "w-[9rem] font-sans text-base", fieldId: "charging-options-dropdown-field", placeholder: "Sorting option(s)", size: "sm", value: sortByFilter, children: _jsxs(CatList, { children: [_jsx(CatListItem, { onBlSelect: () => {
                                                            setSortByFilter("fastest");
                                                        }, value: {
                                                            label: "Fastest",
                                                            value: "Fastest"
                                                        } }), _jsx(CatListItem, { onBlSelect: () => {
                                                            setSortByFilter("fully charged");
                                                        }, value: {
                                                            label: "Fully charged",
                                                            value: "Fully charged"
                                                        } }), _jsx(CatListItem, { onBlSelect: () => {
                                                            setSortByFilter("closest ess");
                                                        }, value: {
                                                            label: "Closest ESS",
                                                            value: "Closest ESS"
                                                        } })] }) }), appConfig?.release_1_1?.showOpsPlannerPhaseTwo &&
                                            appConfig?.release_1_1?.showSocFutureChargingOpsPlanner && (_jsxs(CatContextualMenu, { onBlContextualMenuOpen: handleSelectTimeClick, position: "bottom", ref: selectTimeRef, children: [_jsx(CatButton, { className: "w-[295px] h-[32px] inline-flex", "data-test": "select-time-button", fullWidth: true, size: "sm", slot: "trigger", children: _jsxs("div", { className: "w-[295px] flex justify-between font-normal items-center px-2 font-sans text-base overflow-hidden", children: [_jsx("span", { className: "text-left pr-6 whitespace-nowrap flex-shrink min-w-0", children: selectedTime }), _jsxs("div", { className: "flex items-center gap-1 flex-shrink-0", children: [selectedTime !== "Select time" && (_jsx("span", { "aria-label": "Clear selected time", className: "inline-flex justify-center items-center pr-1 h-3 w-3 cursor-pointer", onClick: (e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            safelyStopImmediatePropagation(e);
                                                                            suppressNextOpenRef.current = true;
                                                                            // Clear form and state
                                                                            setValue("time", "");
                                                                            setSelectedTime("Select time");
                                                                            setShowFutureChargingOptionDetails(false);
                                                                            setFetchFutureChargeOptions(false);
                                                                            setIsLoadingFutureChargingOptionDetails(false);
                                                                            setIsReloadingChargingOptions(true);
                                                                            setIsUsingPersistedData(false);
                                                                            // Reset refs to allow hydration after clear
                                                                            userChangedTimePanelRef.current = false;
                                                                            staleRefetchTriggeredRef.current = false;
                                                                            isTimePanelClosedRef.current = true;
                                                                            // Persist clear state
                                                                            persistTime("opsPlanner", {
                                                                                selectedTimeLabel: "Select time",
                                                                                showFuture: false,
                                                                                time: "",
                                                                                timePanel: selectedPanelRef.current,
                                                                                timeUnits: timeUnits
                                                                            });
                                                                            refetchChargingOptions();
                                                                        }, onMouseDown: (e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            safelyStopImmediatePropagation(e);
                                                                            suppressNextOpenRef.current = true;
                                                                        }, role: "button", children: _jsx(CatIconClose, { className: "h-[16px] w-[16px] mr-2 ml-2 mb-0" }) })), _jsx(CatIconClock, { className: "inline-flex justify-center pl-1 h-3.5" })] })] }) }), _jsxs("div", { className: "w-[335px]", children: [_jsx("h2", { className: "w-full h-[40px] flex items-center justify-center", children: "Time" }), _jsx("hr", {}), _jsx("div", { className: "flex p-2", children: _jsxs(CatSegmentedControl, { activeIndex: timePanelActiveIndexRef.current, className: "relative", onBlSegmentItemChange: timePanelChange, children: [_jsx(CatSegmentedControlItem, { className: "flex-1", "data-test": "departure-tab", children: "Departure" }), _jsx(CatSegmentedControlItem, { className: "flex-1", "data-test": "arrival-diagram-tab", children: "Arrival" }), _jsx(CatSegmentedControlItem, { className: "flex-1", "data-test": "completion-diagram-tab", children: "Completion" })] }) }), _jsx("hr", {}), _jsxs("form", { onSubmit: (e) => {
                                                                e.preventDefault();
                                                                if (!timeError) {
                                                                    handleApply();
                                                                }
                                                            }, children: [_jsxs("div", { className: "flex items-start gap-2 p-2", children: [_jsxs("div", { className: "flex flex-col w-full min-h-[64px]", children: [_jsx("label", { className: "text-xs font-semibold mb-1", children: "Time (required)" }), _jsx(Controller, { control: control, name: "time", render: ({ field: { onBlur, onChange, value } }) => (_jsx(CatInputField, { "data-test": "asset-name", errorNote: timeError, fieldId: "text-field-11", onBlChange: (e) => {
                                                                                            onChange(e);
                                                                                            // Validate as user types
                                                                                            const inputValue = e.detail?.value ||
                                                                                                e.target?.value ||
                                                                                                "";
                                                                                            if (inputValue.trim()) {
                                                                                                const { canProceed, error } = validateFutureCESTTime(inputValue, timeUnits);
                                                                                                if (!canProceed && error) {
                                                                                                    setTimeError(error);
                                                                                                }
                                                                                                else {
                                                                                                    setTimeError("");
                                                                                                }
                                                                                            }
                                                                                            else {
                                                                                                setTimeError("");
                                                                                            }
                                                                                        }, onBlur: onBlur, onKeyDown: (e) => {
                                                                                            if (e.key === "Enter") {
                                                                                                e.preventDefault();
                                                                                                if (!timeError) {
                                                                                                    handleApply();
                                                                                                }
                                                                                            }
                                                                                        }, placeholder: getPlaceholderTime(projectMetaData?.timezone), size: "sm", status: timeError ? "error" : undefined, value: value })) }), timeError && (_jsx("div", { className: "text-xs text-red-600 mt-1", "data-test": "time-error-text", children: timeError }))] }), _jsxs("div", { className: "flex flex-col w-[80px]", children: [_jsx("label", { className: "text-xs font-semibold mb-1", children: "AM/PM" }), _jsxs("select", { className: "border border-gray-300 rounded px-1 py-1", onChange: (event) => {
                                                                                        const newTimeUnits = event.target.value;
                                                                                        if (timeUnits !== newTimeUnits) {
                                                                                            setTimeUnits(newTimeUnits);
                                                                                            // Revalidate time when AM/PM changes
                                                                                            const currentTime = getValues("time");
                                                                                            if (currentTime?.trim()) {
                                                                                                const { canProceed, error } = validateFutureCESTTime(currentTime, newTimeUnits);
                                                                                                if (!canProceed && error) {
                                                                                                    setTimeError(error);
                                                                                                }
                                                                                                else {
                                                                                                    setTimeError("");
                                                                                                }
                                                                                            }
                                                                                        }
                                                                                    }, value: timeUnits, children: [_jsx("option", { children: "AM" }), _jsx("option", { children: "PM" })] })] })] }), _jsxs("div", { className: "flex justify-start py-4", children: [_jsx(CatButton, { className: "pl-4", "data-testid": "Apply-button", disabled: !!timeError, onClick: handleApply, type: "submit", variant: "primary", children: "Apply" }), _jsx(CatButton, { href: "#", onClick: () => {
                                                                                selectTimeRef.current.close();
                                                                            }, size: "sm", type: "button", variant: "ghost", children: "Cancel" })] })] })] })] }))] }) })] }), getOpsChargingDetails()] }), _jsx(ChargingOptionDrawer, { chargingDrawerActive: chargingDrawerActive, onChargingDrawerClose: onChargingDrawerClose, selectedOptionDetail: selectedOptionDetail })] }));
};
export default OpsPlanner;
